import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Table, Alert, Spinner, Badge, Tabs, Tab, Modal, FloatingLabel, InputGroup } from 'react-bootstrap';
import { get, post } from '../../services/apiController';
import moment from 'moment';
import { toast, ToastContainer } from 'react-toastify';
import PaginationInPage from '../Pagination';
import { FiUpload, FiDownload, FiRefreshCw, FiSearch, FiEye } from 'react-icons/fi';
import { saveAs } from 'file-saver';

const FinishedGoodsSummary = () => {
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [importLogs, setImportLogs] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [stats, setStats] = useState({
    totalReports: 0,
    reportsByType: [],
    latestImport: null
  });
  const [filters, setFilters] = useState({
    fromDate: moment().subtract(6, 'days').format('YYYY-MM-DD'),
    toDate: moment().format('YYYY-MM-DD'),
    fromLocation: '',
    itemType: ''
  });
  const [activeTab, setActiveTab] = useState('reports');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const TABLE_DEFAULT_LIMIT = 50;
  const [excelLoading, setExcelLoading] = useState(false);

  // Fetch reports and stats on component mount
  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReports();
      fetchStats();
    }
    if (activeTab === 'imports') {
      fetchImportLogs();
    }
  }, [activeTab, currentPage]);

  // Fetch finished goods summary reports from database
  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = [];
      if (filters.fromDate) params.push(`fromDate=${filters.fromDate}`);
      if (filters.toDate) params.push(`toDate=${filters.toDate}`);
      if (filters.fromLocation) params.push(`fromLocation=${encodeURIComponent(filters.fromLocation)}`);
      if (filters.itemType) params.push(`itemType=${encodeURIComponent(filters.itemType)}`);
      params.push(`page=${currentPage}`);
      params.push(`limit=${TABLE_DEFAULT_LIMIT}`);

      const response = await get(`/inv/finished-goods/summary-reports?${params.join('&')}`);
      if (response?.success && response?.data) {
        setReports(response.data.rows || []);
        setTotalRecords(response.data.count || 0);
      } else {
        setReports([]);
        setTotalRecords(0);
        if (response?.message) {
          toast.error(`Error fetching summary reports: ${response.message}`);
        }
      }
    } catch (error) {
      console.error('Error fetching finished goods summary reports:', error);
      toast.error(`Failed to fetch summary reports: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch finished goods summary statistics
  const fetchStats = async () => {
    try {
      const response = await get('/inv/finished-goods/summary-stats');

      if (response?.success && response?.data) {
        setStats(response.data);
      } else if (response?.message) {
        toast.error(`Error fetching summary stats: ${response.message}`);
      }
    } catch (error) {
      console.error('Error fetching finished goods summary stats:', error);
      toast.error(`Failed to fetch summary stats: ${error.message || 'Unknown error'}`);
    }
  };

  // Fetch import logs
  const fetchImportLogs = async () => {
    try {
      const response = await get(`/inv/finished-goods/summary-import-logs?page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}`);

      if (response?.success && response?.data?.logs) {
        setImportLogs(response.data.logs);
        setTotalRecords(response.data.pagination?.total || 0);
      } else if (response?.message) {
        toast.error(`Error fetching summary import logs: ${response.message}`);
      }
    } catch (error) {
      console.error('Error fetching summary import logs:', error);
      toast.error(`Failed to fetch summary import logs: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle file selection for import
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // Handle file import
  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to import');
      return;
    }
    setImportLoading(true);
    const formData = new FormData();
    formData.append('finishedGoodsSummaryFile', selectedFile);
    post(formData, '/inv/finished-goods/summary-upload', false).then(res => {
      console.log(res);
      toast.success('File imported successfully');
      setShowImportModal(false);
      setSelectedFile(null);
      fetchImportLogs();
      fetchReports();
    }).catch(err => {
      toast.error(`Import failed: ${err?.response?.data?.message || err?.message || 'Unknown error'}`);
    }).finally(() => {
      setImportLoading(false);
    })
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
    setCurrentPage(1);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('DD-MM-YYYY HH:mm:ss');
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'danger';
      default: return 'secondary';
    }
  };

  // Excel download handler
  const handleExcelDownload = async () => {
    setExcelLoading(true);
    const params = [];
    if (filters.fromDate) params.push(`fromDate=${filters.fromDate}`);
    if (filters.toDate) params.push(`toDate=${filters.toDate}`);
    if (filters.fromLocation) params.push(`fromLocation=${encodeURIComponent(filters.fromLocation)}`);
    if (filters.itemType) params.push(`itemType=${encodeURIComponent(filters.itemType)}`);
    const url = `/inv/finished-goods/summary-reports/excel?${params.join('&')}`;
    get(url, { responseType: 'arraybuffer' })
      .then((res) => {
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const fileName = `Finished_Goods_Summary_Report_${filters.fromDate}_to_${filters.toDate}.xlsx`;
        saveAs(blob, fileName);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.data?.message || err?.message);
      }).finally(() => {
        setExcelLoading(false);
      })
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="content-title-wrpper mb-3">
        <Card className="w-100">
          <Card.Header className="bg-white">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => {
                setActiveTab(k);
                setCurrentPage(1);
              }}
              className="mb-0"
            >
              <Tab eventKey="reports" title="Finished Goods Summary Reports" />
              <Tab eventKey="imports" title="Import Logs" />
            </Tabs>
          </Card.Header>

          {activeTab === 'reports' && (
            <>
              <Card.Body className="px-0 pt-0">
                <Row className="mx-0">
                  <Col md={6} className="p-3">
                    <Card className="mb-0">
                      <Card.Header className="py-2">
                        <span className="fw-bold">Report Statistics</span>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Row>
                          <Col md={6}>
                            <h5 className="fs-6 fw-bold">Total Reports</h5>
                            <h3 className="fs-4">{stats.totalReports || 0}</h3>
                          </Col>
                          <Col md={6}>
                            <h5 className="fs-6 fw-bold">Latest Import</h5>
                            <p className="mb-0">{stats.latestImport ? moment(stats.latestImport).format('DD-MM-YYYY HH:mm:ss') : 'Never'}</p>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col>
                            <h5 className="fs-6 fw-bold">Report Types</h5>
                            <div>
                              {stats.reportsByType && stats.reportsByType.map((type, index) => (
                                <Badge key={index} bg="info" className="me-2 mb-2">
                                  {type.item_type}: {type.count}
                                </Badge>
                              ))}
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6} className="p-3">
                    <Card className="mb-0">
                      <Card.Header className="py-2">
                        <span className="fw-bold">Date Range Filter</span>
                      </Card.Header>
                      <Card.Body className="py-3">
                        <Form>
                          <Row>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="small">From Date</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={filters.fromDate}
                                  onChange={e => handleFilterChange('fromDate', e.target.value)}
                                  max={filters.toDate}
                                  className="form-control-sm"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label className="small">To Date</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={filters.toDate}
                                  onChange={e => handleFilterChange('toDate', e.target.value)}
                                  min={filters.fromDate}
                                  max={moment().format('YYYY-MM-DD')}
                                  className="form-control-sm"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Form>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>

              {/* Filters Row */}
              <Card.Body className="pb-0 pt-3">
                <Row className="mb-2">
                  <Col md={3} sm={6} xs={12} className="mb-2">
                    <InputGroup>
                      <InputGroup.Text><FiSearch /></InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="From Location"
                        value={filters.fromLocation}
                        onChange={e => handleFilterChange('fromLocation', e.target.value)}
                        className="form-control-sm"
                      />
                    </InputGroup>
                  </Col>
                  <Col md={3} sm={6} xs={12} className="mb-2">
                    <Form.Select
                      value={filters.itemType}
                      onChange={e => handleFilterChange('itemType', e.target.value)}
                      className="form-control-sm"
                    >
                      <option value="">All Item Types</option>
                      <option value="MENU">MENU</option>
                      <option value="ATTAR (FRAGRANCE)">ATTAR (FRAGRANCE)</option>
                      <option value="BAKERY ITEMS">BAKERY ITEMS</option>
                    </Form.Select>
                  </Col>
                  <Col md={3} sm={6} xs={12} className="mb-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={fetchReports}
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" animation="border" /> : <><FiRefreshCw className="me-1" /> Fetch Reports</>}
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
              {/* End Filters Row */}

              <Card.Body className="px-0 vehicle-requests-table pt-0 pb-0">
                <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                  <span className="fw-bold">Finished Goods Summary Reports | {totalRecords ?? 0}</span>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowImportModal(true)}
                    >
                      <FiUpload className="me-1" /> Import Excel
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={handleExcelDownload}
                      disabled={excelLoading}
                    >
                      {excelLoading ? <Spinner size="sm" animation="border" /> : <><FiDownload className="me-1" /> Download Excel</>}
                    </Button>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    {loading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading summary reports...</p>
                      </div>
                    ) : reports.length === 0 ? (
                      <Alert variant="info" className="m-3">No finished goods summary reports found for the selected filters</Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table responsive striped hover>
                          <thead>
                            <tr>
                              <th>S.No</th>
                              <th>From Location</th>
                              <th>Item Code</th>
                              <th>Item Name</th>
                              <th>Type</th>
                              <th>Item Qty (UOM)</th>
                              <th>CP</th>
                              <th>CP Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map((report, index) => (
                              <tr key={index}>
                                <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                                <td>{report.from_location || 'N/A'}</td>
                                <td>{report.item_code || 'N/A'}</td>
                                <td>{report.item_name || 'N/A'}</td>
                                <td>
                                  <Badge bg="secondary" className="small">
                                    {report.item_type || 'N/A'}
                                  </Badge>
                                </td>
                                <td>{report.item_qty ? `${report.item_qty} ${report.uom || ''}` : 'N/A'}</td>
                                <td>{report.cp || 0}</td>
                                <td>{report.cp_value || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        <PaginationInPage
                          currentPage={currentPage}
                          setCurrentPage={setCurrentPage}
                          totalRecords={totalRecords}
                          TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </>
          )}

          {activeTab === 'imports' && (
            <Card.Body className="px-0 vehicle-requests-table pt-0 pb-0">
              <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                <span className="fw-bold">Import Logs</span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowImportModal(true)}
                >
                  <FiUpload className="me-1" /> Import Excel
                </Button>
              </div>
              <div className="row">
                <div className="col">
                  <Table responsive striped hover>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Filename</th>
                        <th>File Type</th>
                        <th>Total Records</th>
                        <th>Successful</th>
                        <th>Failed</th>
                        <th>Status</th>
                        <th>Imported By</th>
                        <th>Import Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importLogs.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center">No import logs found</td>
                        </tr>
                      ) : (
                        importLogs.map((log, index) => (
                          <tr key={log.id}>
                            <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                            <td>{log.filename}</td>
                            <td>{log.file_type}</td>
                            <td>{log.total_records}</td>
                            <td>{log.successful_imports}</td>
                            <td>{log.failed_imports}</td>
                            <td>
                              <Badge bg={getStatusBadge(log.status)}>
                                {log.status}
                              </Badge>
                            </td>
                            <td>{log.imported_by_user?.name || log.imported_by}</td>
                            <td>{formatDate(log.createdAt)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                  <PaginationInPage
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalRecords={totalRecords}
                    TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
                  />
                </div>
              </div>
            </Card.Body>
          )}
        </Card>

        {/* Import Modal */}
        <Modal show={showImportModal} onHide={() => setShowImportModal(false)} backdrop="static" keyboard={false} className="request-modal">
          <Modal.Header>
            <Modal.Title>Import Finished Goods Summary Data</Modal.Title>
            <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowImportModal(false)}></button>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Select Excel File</Form.Label>
                <Form.Control
                  type="file"
                  onChange={handleFileChange}
                  accept=".xlsx, .xls"
                />
                <Form.Text className="text-muted">
                  Supported formats: XLS, XLSX
                </Form.Text>
              </Form.Group>
            </Form>
            <div className="text-center mt-4">
              <Button
                variant="primary"
                onClick={handleImport}
                disabled={importLoading || !selectedFile}
                className="add-request-btn"
              >
                {importLoading ? <Spinner size="sm" animation="border" /> : 'Import'}
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default FinishedGoodsSummary; 